import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { AgEditorComponent } from '../../../../global/gx-ag-grid/ag-editor.component';
import { copyContentToClipboard } from '../../../../../utils/miscellaneous';
import { FeedbackService } from '../../../../../core/feedback.service';
import { FormControl, FormGroup, ValidatorFn, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { excludeWhitespaceOnly, ValidationErrorKeys } from '../../../edit-location/service/validators';
import { REQUIRED_FIELDS } from '../../../constants';
import { ICellEditorParams } from 'ag-grid-community';
import { Location } from '../../../../../models/locations-models/location';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { DblclickPrependTodayDirective } from '../../../../../directives/dblclick-prepend-today.directive';
import { GxInputDirective } from '../../../../../directives/gxinput.directive';
import { CdkTextareaAutosize } from '@angular/cdk/text-field';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatError } from '@angular/material/form-field';
import { NgClass } from '@angular/common';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { LetDirective } from '@ngrx/component';
interface SimpleTextEditorFormGroup {
  [key: string]: FormControl<string>
}

/**
 * This component will be rendered as an edit popup with auto-height (min height 3 rows,
 * max height 15 rows).
 * Has a copy-to-clipboard feature.
 * Requires to define 'field' (and a valueGetter if needed) property in grid ColDefs.
 * Do define suppressKeyboardEvent() callback in grid colDefs if being able to add a newline
 * is required.
 */
@Component({
    selector: 'app-simple-text-editor',
    templateUrl: './simple-text-editor.component.html',
    styleUrls: ['./simple-text-editor.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [LetDirective, FormsModule, CdkTrapFocus, ReactiveFormsModule, NgClass, MatFormField, MatInput, CdkTextareaAutosize, GxInputDirective, DblclickPrependTodayDirective, MatIcon, MatError, MatTooltip]
})
export class SimpleTextEditorComponent extends AgEditorComponent<Location> {

  @ViewChild('textInput') textInput: ElementRef;

  constructor(protected feedbackService: FeedbackService, protected cd: ChangeDetectorRef) {
    super(feedbackService, cd);
  }

  protected override createEditorFormGroup(params: ICellEditorParams<Location>): FormGroup<SimpleTextEditorFormGroup> {
    const validators: ValidatorFn[] = [excludeWhitespaceOnly];

    if ((REQUIRED_FIELDS as string[]).includes(params.colDef.field)) {
      validators.push(Validators.required);
    }

    return new FormGroup({
      [params?.colDef?.field]: new FormControl(params.value, {
        nonNullable: true,
        validators
      }),
    });
  }

  protected getErrorMessage(params: ICellEditorParams<Location>): string {
    const controls = this.editorFormGroup.controls;
    const { errors } = controls[params?.colDef?.field];

    const errorKeys: string[] = Object.keys(errors || []);

    // nothing to return..
    if (!errorKeys.length) {
      return null;
    }

    const firstErrorKey = errorKeys[0];

    switch (firstErrorKey) {
      case ValidationErrorKeys.REQUIRED:
        return 'Required field.';
      case ValidationErrorKeys.CONTAINS_ONLY_WHITESPACE:
        return 'Cannot be whitespace-only.';
      default:
        return 'Please check your input.';
    }
  }

  /**
   * Copy the textarea input to clipboard.
   * @param textarea Comment Textarea reference
   */
  copyTextInput(textarea: HTMLTextAreaElement) {
    copyContentToClipboard(textarea, (ok) => {
      ok && this.feedbackService.showSuccessSnackbar({
        data: { userFriendlyMessage: 'Copied' },
        isAddressListSnackbar: true,
      });
    });
  }

  /**
   * A hook to perform any necessary operation just after the GUI for this component has been
   * rendered on the screen.
   * This method is called each time the edit component is activated.
   * This is useful for any logic that requires attachment before executing, such as putting focus
   * on a particular DOM element.
   */
  afterGuiAttached(): void {
    this.textInput?.nativeElement?.focus();
  }

}
